import React, { useState, useMemo, useEffect } from "react";
import {
  Tabs,
  Tab,
  Box,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Card,
  CardContent,
  Grid,
} from "@mui/material";
import {
  TrendingUp as StocksIcon,
  AccountBalance as BondsIcon,
  MonetizationOn as CashIcon,
  Category as StructuredIcon,
} from "@mui/icons-material";
import StyledDrawer from "../../shared/UIParts/StyledDrawer";
import { ManualMimshakData } from "../../interfaces/DTO/manualMimshakData";
import { formatCurrency } from "../../utils/namesFormater";
import { formatProductTypeUniversal } from "../../utils/productTypeUniversalFormatter";
import { formatHandlerCodeUniversal } from "../../utils/handlerCodeUniversalFormatter";
import { colorProductTypeUniversal, colorHandlerCodeUniversal } from "../../utils/colorEnumFormatter";
import { formatInvestmentPathType, getInvestmentPathColor } from "../../utils/investmentPathTypeFormatter";
import { InvestmentPathDetail } from "../../interfaces/DTO/mislaka_xml_models";
import { InvestmentPathType } from "../../interfaces/DTO/investmentPathType";

interface InvestmentPortfolioDrawerProps {
  manualMimshakData: ManualMimshakData | null;
  onClose: () => void;
  contactName?: string;
}

/**
 * מגירה להצגת פרטי תיק השקעות
 * מציגה טאב ראשון של מסלולים עם פירוט לכל מסלול השקעה
 */
const InvestmentPortfolioDrawer: React.FC<InvestmentPortfolioDrawerProps> = ({
  manualMimshakData,
  onClose,
  contactName,
}) => {
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // חילוץ נתוני מסלולי ההשקעה מתוך ה-data
  const investmentPaths = useMemo(() => {
    if (!manualMimshakData?.data?.products?.[0]?.policies_or_accounts?.[0]?.investment_paths) {
      return [];
    }
    return manualMimshakData.data.products[0].policies_or_accounts[0].investment_paths;
  }, [manualMimshakData]);

  // חישוב סך כל הסכום
  const totalAmount = useMemo(() => {
    return investmentPaths.reduce((sum, path) => sum + (path.accumulated_amount || 0), 0);
  }, [investmentPaths]);

  // חישוב אחוז מהתיק לכל מסלול
  const pathsWithPercentage = useMemo(() => {
    return investmentPaths.map((path) => ({
      ...path,
      percentage: totalAmount > 0 ? (path.accumulated_amount / totalAmount) * 100 : 0,
    }));
  }, [investmentPaths, totalAmount]);

  // חישוב סיכומים לפי קטגוריות
  const categorySummary = useMemo(() => {
    const stocks = investmentPaths
      .filter(
        (p) =>
          p.path_code === InvestmentPathType.ISRAEL_STOCKS ||
          p.path_code === InvestmentPathType.FOREIGN_STOCKS
      )
      .reduce((sum, p) => sum + (p.accumulated_amount || 0), 0);

    const bonds = investmentPaths
      .filter(
        (p) =>
          p.path_code === InvestmentPathType.CORPORATE_INDEXED_BONDS ||
          p.path_code === InvestmentPathType.GOVT_INDEXED_BONDS ||
          p.path_code === InvestmentPathType.GOVT_SHEKEL_BONDS ||
          p.path_code === InvestmentPathType.CORPORATE_SHEKEL_BONDS ||
          p.path_code === InvestmentPathType.FOREIGN_BONDS
      )
      .reduce((sum, p) => sum + (p.accumulated_amount || 0), 0);

    const cash = investmentPaths
      .filter(
        (p) =>
          p.path_code === InvestmentPathType.CASH ||
          p.path_code === InvestmentPathType.FOREIGN_CURRENCY
      )
      .reduce((sum, p) => sum + (p.accumulated_amount || 0), 0);

    const structured = investmentPaths
      .filter((p) => p.path_code === InvestmentPathType.STRUCTURED_PRODUCTS)
      .reduce((sum, p) => sum + (p.accumulated_amount || 0), 0);

    return {
      stocks,
      stocksPercent: totalAmount > 0 ? (stocks / totalAmount) * 100 : 0,
      bonds,
      bondsPercent: totalAmount > 0 ? (bonds / totalAmount) * 100 : 0,
      cash,
      cashPercent: totalAmount > 0 ? (cash / totalAmount) * 100 : 0,
      structured,
      structuredPercent: totalAmount > 0 ? (structured / totalAmount) * 100 : 0,
    };
  }, [investmentPaths, totalAmount]);

  if (!manualMimshakData) return null;

  const productTypeColor = colorProductTypeUniversal(manualMimshakData.product_type);
  const handlerColor = colorHandlerCodeUniversal(manualMimshakData.handler_code);

  return (
    <StyledDrawer open={!!manualMimshakData} onClose={onClose} width={1000}>
      {/* כותרת עם פרטי המוצר */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          mb: 3,
          pb: 2,
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        {/* סוג מוצר */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            סוג מוצר:
          </Typography>
          <Chip
            label={formatProductTypeUniversal(manualMimshakData.product_type)}
            sx={{ backgroundColor: productTypeColor, color: "white" }}
          />
        </Box>

        {/* מספר פוליסה */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            מספר תיק:
          </Typography>
          <Typography variant="body1" fontWeight="bold">
            {manualMimshakData.policy_number}
          </Typography>
        </Box>

        {/* יצרן */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            בית השקעות:
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 10,
                height: 30,
                borderRadius: "20%",
                backgroundColor: handlerColor,
              }}
            />
            <Typography variant="body1">
              {formatHandlerCodeUniversal(manualMimshakData.handler_code)}
            </Typography>
          </Box>
        </Box>

        {/* שם לקוח */}
        {contactName && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              לקוח:
            </Typography>
            <Typography variant="body1">{contactName}</Typography>
          </Box>
        )}

        {/* נכון לתקופה */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            נכון לתקופה:
          </Typography>
          <Typography variant="body1">{manualMimshakData.reported_period}</Typography>
        </Box>

        {/* סכום כולל */}
        <Box
          sx={{
            textAlign: "center",
            py: 2,
            mt: 2,
            backgroundColor: "#f5f5f5",
            borderRadius: 2,
          }}
        >
          <Typography variant="h4" fontWeight="bold" color="primary">
            <span dir="ltr">{formatCurrency(manualMimshakData.funds_sum || 0)}</span>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            סה"כ שווי התיק
          </Typography>
        </Box>
      </Box>

      {/* קוביות מטריקות - סיכום לפי קטגוריות */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* מניות */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={1}
            sx={{
              backgroundColor: "#f5f5f5",
              borderLeft: "4px solid #1976d2",
              height: "100%",
            }}
          >
            <CardContent sx={{ py: 2.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                <StocksIcon sx={{ fontSize: 28, color: "#1976d2" }} />
                <Typography variant="subtitle1" fontWeight={600} color="text.secondary">
                  מניות
                </Typography>
              </Box>
              <Typography variant="h6" fontWeight="bold" color="text.primary">
                <span dir="ltr">{formatCurrency(categorySummary.stocks)}</span>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {categorySummary.stocksPercent.toFixed(1)}% מהתיק
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* מוצרים מובנים */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={1}
            sx={{
              backgroundColor: "#f5f5f5",
              borderLeft: "4px solid #7b1fa2",
              height: "100%",
            }}
          >
            <CardContent sx={{ py: 2.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                <StructuredIcon sx={{ fontSize: 28, color: "#7b1fa2" }} />
                <Typography variant="subtitle1" fontWeight={600} color="text.secondary">
                  מוצרים מובנים
                </Typography>
              </Box>
              <Typography variant="h6" fontWeight="bold" color="text.primary">
                <span dir="ltr">{formatCurrency(categorySummary.structured)}</span>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {categorySummary.structuredPercent.toFixed(1)}% מהתיק
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* אג"ח */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={1}
            sx={{
              backgroundColor: "#f5f5f5",
              borderLeft: "4px solid #f57c00",
              height: "100%",
            }}
          >
            <CardContent sx={{ py: 2.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                <BondsIcon sx={{ fontSize: 28, color: "#f57c00" }} />
                <Typography variant="subtitle1" fontWeight={600} color="text.secondary">
                  אג"ח
                </Typography>
              </Box>
              <Typography variant="h6" fontWeight="bold" color="text.primary">
                <span dir="ltr">{formatCurrency(categorySummary.bonds)}</span>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {categorySummary.bondsPercent.toFixed(1)}% מהתיק
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* מזומנים */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={1}
            sx={{
              backgroundColor: "#f5f5f5",
              borderLeft: "4px solid #388e3c",
              height: "100%",
            }}
          >
            <CardContent sx={{ py: 2.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                <CashIcon sx={{ fontSize: 28, color: "#388e3c" }} />
                <Typography variant="subtitle1" fontWeight={600} color="text.secondary">
                  מזומנים
                </Typography>
              </Box>
              <Typography variant="h6" fontWeight="bold" color="text.primary">
                <span dir="ltr">{formatCurrency(categorySummary.cash)}</span>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {categorySummary.cashPercent.toFixed(1)}% מהתיק
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* טאבים */}
      <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
        <Tab label={`מסלולי השקעה (${investmentPaths.length})`} />
      </Tabs>

      {/* טאב ראשון - מסלולי השקעה */}
      {activeTab === 0 && (
        <Box>
          {investmentPaths.length === 0 ? (
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ textAlign: "center", py: 4 }}
            >
              לא נמצאו מסלולי השקעה
            </Typography>
          ) : (
            <>
              {/* הצגת דמי ניהול מעל הטבלה אם קיימים */}
              {(() => {
                const totalManagementFeeDeposit = investmentPaths.reduce(
                  (sum, p) => sum + (p.management_fee_on_deposit || 0),
                  0
                );
                const totalManagementFeeSavings = investmentPaths.reduce(
                  (sum, p) => sum + (p.management_fee_on_savings || 0),
                  0
                );
                const hasManagementFees =
                  totalManagementFeeDeposit > 0 || totalManagementFeeSavings > 0;

                if (!hasManagementFees) return null;

                return (
                  <Box
                    sx={{
                      mb: 2,
                      p: 2,
                      backgroundColor: "#fff3e0",
                      borderRadius: 1,
                      border: "1px solid #ff9800",
                    }}
                  >
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                      דמי ניהול:
                    </Typography>
                    <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                      {totalManagementFeeDeposit > 0 && (
                        <Typography variant="body2">
                          דמי ניהול על הפקדה:{" "}
                          <strong>{totalManagementFeeDeposit.toFixed(2)}%</strong>
                        </Typography>
                      )}
                      {totalManagementFeeSavings > 0 && (
                        <Typography variant="body2">
                          דמי ניהול על חיסכון:{" "}
                          <strong>{totalManagementFeeSavings.toFixed(2)}%</strong>
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              })()}

              <TableContainer component={Paper} elevation={2}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                      <TableCell align="center" sx={{ fontWeight: "bold" }}>
                        מסלול השקעה
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: "bold" }}>
                        סכום
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: "bold" }}>
                        אחוז מהתיק
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pathsWithPercentage.map((path, index) => {
                      const pathColor = getInvestmentPathColor(path.path_code);

                      return (
                        <TableRow
                          key={index}
                          sx={{
                            "&:hover": { backgroundColor: "#f9f9f9" },
                          }}
                        >
                          {/* שם המסלול */}
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Box
                                sx={{
                                  width: 12,
                                  height: 12,
                                  borderRadius: "50%",
                                  backgroundColor: pathColor,
                                  flexShrink: 0,
                                }}
                              />
                              <Typography variant="body2" fontWeight={500}>
                                {formatInvestmentPathType(path.path_code)}
                              </Typography>
                            </Box>
                          </TableCell>

                          {/* סכום */}
                          <TableCell align="center">
                            <Typography variant="body1" fontWeight="bold">
                              <span dir="ltr">
                                {formatCurrency(path.accumulated_amount || 0)}
                              </span>
                            </Typography>
                          </TableCell>

                          {/* אחוז מהתיק */}
                          <TableCell align="center">
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                              <Typography variant="body2" fontWeight={600}>
                                {path.percentage.toFixed(2)}%
                              </Typography>
                              <LinearProgress
                                variant="determinate"
                                value={path.percentage}
                                sx={{
                                  height: 8,
                                  borderRadius: 1,
                                  backgroundColor: "#e0e0e0",
                                  "& .MuiLinearProgress-bar": {
                                    backgroundColor: pathColor,
                                  },
                                }}
                              />
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}

                    {/* שורת סיכום */}
                    <TableRow sx={{ backgroundColor: "#f0f0f0" }}>
                      <TableCell>
                        <Typography variant="body1" fontWeight="bold">
                          סה"כ
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body1" fontWeight="bold">
                          <span dir="ltr">{formatCurrency(totalAmount)}</span>
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body1" fontWeight="bold">
                          100%
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Box>
      )}
    </StyledDrawer>
  );
};

export default InvestmentPortfolioDrawer;
